import React from 'react';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/hooks';
import { type InsightEntitlement, type TCodeData } from '../../../shared/types';

import './InsightsDetails.css';

/**
 * Props interface for the InsightsDetails component
 */
interface InsightsDetailsProps {
  selectedTCode: string;
  tCodeData: TCodeData[];
  entitlements?: InsightEntitlement[];
  onBack: () => void;
  className?: string;
}

/**
 * InsightsDetails Component
 * Displays detailed information about a selected T-code.
 * Shows code information, impact analysis, and recommendations.
 */
function InsightsDetails({
  selectedTCode,
  tCodeData,
  entitlements,
  onBack,
  className,
}: InsightsDetailsProps): React.ReactElement {
  const l = useInvertedLocalize();
  const selectedTCodeInfo = tCodeData.find((item) => item.id === selectedTCode);

  return (
    <Box className={className}>
      {/* Header */}
      <Box className='Sod-InsightsDetails-header'>
        <Box className='Sod-InsightsDetails-header-back-icon'>
          <ButtonIcon
            size='small'
            kind='ghost'
            ariaLabel={l('Back to insights list')}
            icon={<Icon kind='ArrowLeft' size='xSmall' />}
            onClick={onBack}
          />
        </Box>
        <Box className='Sod-InsightsDetails-header-front-spacing' />
        <Typography
          lineLimit={3}
          kind='h3'
          className='Sod-InsightsDetails-header-title'>
          {selectedTCodeInfo?.description ?? l('Entitlement Details')}
        </Typography>
      </Box>

      {/* Content */}
      <Box className='Sod-InsightsDetails-content'>
        {/* Entitlements List - show all, preserve API order */}
        {Array.isArray(entitlements) && entitlements.length > 0 ? (
          entitlements.map((e) => (
            <Box
              key={e.entitlementDisplayName}
              className='Sod-InsightsDetails-section'>
              <Box className='Sod-InsightsDetails-item'>
                <Box className='Sod-InsightsDetails-item-hover-area'>
                  <Box className='Sod-InsightsDetails-item-hover-area-front-tcode' />
                  <Box className='Sod-InsightsDetails-item-hover-area-heading-description'>
                    <Box className='Sod-InsightsDetails-item-hover-area-heading-description-name'>
                      <Typography lineLimit={3} kind='h3'>
                        {e.entitlementDisplayName}
                      </Typography>
                      {/* removed literal dot */}
                      {e.entitlementDeviation ? (
                        <Box className='Sod-InsightsDetails-item-hover-area-heading-description-name-chip'>
                          <Chip
                            kind='outlined'
                            size='xxSmall'
                            label={l('Deviate')}
                            color='critical'
                            leftIcon={
                              <Icon
                                kind='AlertCriticalOutline'
                                color='critical'
                              />
                            }
                          />
                        </Box>
                      ) : null}
                    </Box>
                    <Box className='Sod-InsightsDetails-item-hover-area-heading-description-description'>
                      <Typography lineLimit={3} kind='body3'>
                        <span className='Sod-InsightsDetails-item-hover-area-heading-description-description-span'>
                          {l('Description:')}
                        </span>
                      </Typography>
                      <Typography lineLimit={3} kind='body3'>
                        {e.entitlementDescription ?? ''}
                      </Typography>
                    </Box>
                    <Box className='Sod-InsightsDetails-item-hover-area-heading-description-additional'>
                      <Typography lineLimit={3} kind='body3'>
                        <span className='Sod-InsightsDetails-item-hover-area-heading-description-additional-span'>
                          {l('Usage Count:')}
                        </span>
                      </Typography>
                      <Typography lineLimit={3} kind='body3'>
                        {e.entitlementUsage === undefined
                          ? l('-')
                          : String(e.entitlementUsage)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography lineLimit={3} kind='body2'>
            {l('No Data to show')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

InsightsDetails.defaultProps = {
  className: undefined,
  entitlements: undefined,
};

export default InsightsDetails;
